import React, { useState } from 'react';

import { Sparklines, SparklinesCurve } from 'react-sparklines';
import { NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import AutoSelectText from '../../common/AutoSelectText';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import Checkbox from '../../common/Checkbox';
import {
  hasAnyPermissionForCompany,
  hasPermission,
  hasPermissionForCompany,
  userIs,
} from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import userTypes from '../../../constants/userTypes';
import KebabMenu from '../../common/KebabMenu';
import Button from '../../common/Button';
import icons from '../../../constants/icons';
import SkuRestrictions from '../../sku/components/SkuRestrictions';
import TerritoryLink from '../../common/links/TerritoryLink';
import WorkflowLink from '../../common/links/WorkflowLink';
import PlatformLink from '../../common/links/PlatformLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import NoAppId from '../../common/NoAppId';
import NoProductCode from '../../common/NoProductCode';
import NoPackageId from '../../common/NoPackageId';
import FormattedDate from '../../common/FormattedDate';
import { useAppSelector } from '../../../hooks';
import { getSkus } from '../../../api/gameApi';

export interface SkuListProps {
  reload: any;
  canRequestKeys: boolean;
  onRequestKeys: (e: any, sku: any) => void;
  gameId: string;
  publisherCompanyId: any;
  onAddSku?: () => void;
  canUploadKeys: boolean;
  handleSteamBulkRequest?: (e: any, items: any[]) => void;
  locked: boolean;
  handleBulkActions?: () => void;
  itemsChecked: any[];
  setItemsChecked: (items: any[]) => void;
  keyProviders: any;
}

function SkuList({
  reload,
  canRequestKeys,
  onRequestKeys,
  gameId,
  publisherCompanyId,
  onAddSku,
  canUploadKeys,
  handleSteamBulkRequest,
  locked,
  handleBulkActions,
  itemsChecked,
  setItemsChecked,
  keyProviders,
}: SkuListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.auth);

  let allColumns = [
    'skuName',
    'platform',
    'steamRef',
    'appId',
    'packageId',
    'packageType',
    'canRequestFromDate',
    'canRequestUntilDate',
    'territories',
    'isArchived',
    'requestActivity',
    'keyActivity',
    'creationDate',
  ];
  if (
    hasPermission(currentUser, permissionTypes.MANAGE_GAMES) ||
    hasPermission(currentUser, permissionTypes.MANAGE_KEYS)
  ) {
    allColumns.push('keyWorkflows');
  }

  const suffixColumns: string[] = [];
  if (canRequestKeys || canUploadKeys) {
    suffixColumns.push('buttons-checkbox');
  }

  const defaultVisibleColumns = [
    'skuName',
    'platform',
    'steamRef',
    'appId',
    'packageId',
    'packageType',
    'territories',
    'isArchived',
    'requestActivity',
    'keyActivity',
    'creationDate',
  ];

  const [skus, setSKUs] = useState<any>();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'skuList-v2-settings',
    getTableSettingsDefaults({
      defaultSort: 'skuName',
      defaultVisibleColumns,
    }),
    { gameId },
    (params) => {
      return getSkus(gameId, params);
    },
    setSKUs,
    reload,
    t('Failed to load skus')
  );

  const canRequestOnSteam = (item: any) => {
    const keyProvider = keyProviders?.find((x: any) => x.id === item.keyProviderId);

    return keyProvider?.steamIntegrationEnabled === true && item.canUploadKeys;
  };

  const renderAppId = (item: any) => {
    if (item.skuAppId) return <AutoSelectText>{item.skuAppId}</AutoSelectText>;
    else if (item.appId)
      return (
        <AutoSelectText
          title={t('This app Id is taken from the game, click to select')}
        >
          {item.appId}
        </AutoSelectText>
      );
    else return <NoAppId />;
  };

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: any[] = [
    {
      Key: 'skuName',
      Label: t('Name'),
      className: 'nowrap',
      Render: (item: any) =>
        hasAnyPermissionForCompany(
          currentUser,
          [permissionTypes.MANAGE_GAMES, permissionTypes.MANAGE_KEYS],
          item.publisherCompanyId
        ) ? (
          <>
            <NavLink to={`/sku/${item.skuId}`}>{item.skuName}</NavLink>
            {item.isArchived && (
              <span className="badge bg-danger ms-3">
                <FontAwesomeIcon icon={icons.ARCHIVED} />
                {t('Archived')}
              </span>
            )}
          </>
        ) : (
          item.skuName
        ),
      Filter: {
        Render: (col: any) =>
          hasPermissionForCompany(
            currentUser,
            permissionTypes.MANAGE_GAMES,
            col.publisherCompanyId
          ) ? (
            <div className="row">
              <div className="col-md-8">
                <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('name...')} testId="sku-name-filter" />
              </div>
              <div className="col-md-4">
                <select
                  className="form-control"
                  value={params.get(`f_isArchived`) || ''}
                  onChange={(el: any) => filterChange('isArchived', el.target.value)}
                >
                  <option value="">{t('All')}</option>
                  <option value="Live">{t('Live')}</option>
                  <option value="Archived">{t('Archived')}</option>
                </select>
              </div>
            </div>
          ) : (
            <>
              <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('name...')} testId="sku-name-filter" />
            </>
          ),
      },
    },
    {
      Key: 'platform',
      Label: t('Platform'),
      Render: (item: any) => (
        <PlatformLink id={item.platformId} name={item.platform}></PlatformLink>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('platform...')} />
        ),
      },
    },
    {
      Key: 'steamRef',
      Label: t('Product code'),
      className: 'autoselect',
      Render: (item: any) =>
        item.steamRef ? (
          <AutoSelectText>{item.steamRef}</AutoSelectText>
        ) : (
          <NoProductCode />
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('Code...')} />
        ),
      },
    },
    {
      Key: 'appId',
      Label: t('App Id'),
      className: 'autoselect',
      Render: renderAppId,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('Id...')} />
        ),
      },
    },
    {
      Key: 'packageId',
      Label: t('Package Id'),
      className: 'autoselect',
      Render: (item: any) =>
        item.packageId ? (
          <AutoSelectText>{item.packageId}</AutoSelectText>
        ) : (
          <NoPackageId />
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('Id...')} />
        ),
      },
    },
    {
      Key: 'packageType',
      Label: t('Package type'),
      Render: (item: any) => item.packageType,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('type...')} />
        ),
      },
    },
    {
      Key: 'keyWorkflows',
      Label: t('Workflows'),
      Render: (item: any) => (
        <div className="restrictions">
          <SkuRestrictions
            isDisallow={item.disallowListedKeyWorkflows}
            restrictions={item.keyWorkflows}
            noDataText={t('No restrictions')}
            render={({ id, name, colour }: any) => (
              <WorkflowLink
                id={id}
                key={id}
                name={name}
                colour={colour}
              ></WorkflowLink>
            )}
          ></SkuRestrictions>
        </div>
      ),
    },
    {
      Key: 'territories',
      Sort: false,
      Label: t('Territories'),
      Render: (item: any) => (
        <div className="restrictions">
          <SkuRestrictions
            isDisallow={item.disallowListedTerritories}
            restrictions={item.territories}
            noDataText={t('No restrictions')}
            render={({ id, name, colour }: any) => (
              <TerritoryLink
                id={id}
                key={id}
                name={name}
                colour={colour}
              ></TerritoryLink>
            )}
          ></SkuRestrictions>
        </div>
      ),
    },
    {
      Key: 'canRequestFromDate',
      Label: t('Can request from'),
      Render: (item: any) => item.canRequestFromDate,
    },
    {
      Key: 'canRequestUntilDate',
      Label: t('Can request until'),
      Render: (item: any) => item.canRequestUntilDate,
    },
    {
      Key: 'requestActivity',
      Label: t('Request activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.requestActivityScore30to60Days,
            item.requestActivityScore7to30Days,
            item.requestActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.requestActivityScore30to60Days +
                item.requestActivityScore7to30Days +
                item.requestActivityScoreLast7Days >
                0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#46b29d" />
        </Sparklines>
      ),
    },
    {
      Key: 'keyActivity',
      Label: t('Key activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.keyActivityScore30to60Days,
            item.keyActivityScore7to30Days,
            item.keyActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.keyActivityScore30to60Days +
                item.keyActivityScore7to30Days +
                item.keyActivityScoreLast7Days >
                0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#bb5ebf" />
        </Sparklines>
      ),
    },
    {
      Key: 'creationDate',
      width: '12%',
      Label: t('Created'),
      Render: (game: any) => <FormattedDate value={game.creationDate} variant="default" />,
    },
    {
      Key: 'buttons-checkbox',
      Resize: false,
      Label: '',
      className: 'text-right no-navigate',
      Sort: false,
      width: '2%',
      Filter: {
        Render: (col: any) =>
          !locked && (
            <>
              <Checkbox
                disabled={!skus}
                checked={false}
                onChange={(checked: boolean) =>
                  checked
                    ? setItemsChecked(skus?.data?.filter((x: any) => x.canUploadKeys) ?? [])
                    : setItemsChecked([])
                }
              ></Checkbox>
            </>
          ),
      },
      Render: (item: any) =>
        !locked && (
          <div className="d-flex align-items-center justify-content-end">
            <KebabMenu
              take={1}
              className="inline-icons"
              buttons={[
                item.canRequestKeys && (
                  <Button
                    testId={`request-sku-key-${item.skuName}`}
                    className="btn btn-default me-2"
                    onClick={(e) => {
                      onRequestKeys(e, item);
                    }}
                    icon={icons.REQUEST_KEYS}
                    text={t('Request keys')}
                  ></Button>
                ),
                canRequestOnSteam(item) && (
                  <Button
                    className="btn btn-default me-2"
                    onClick={(e) => handleSteamBulkRequest?.(e, [item])}
                    icon={icons.REQUEST_ON_STEAM}
                    text={t('Request on Steam')}
                  />
                ),
              ]}
            ></KebabMenu>

            {item.canUploadKeys && (
              <>
                <Checkbox
                  className="ms-3"
                  checked={
                    itemsChecked &&
                    itemsChecked.some((x: any) => x.skuId === item.skuId)
                  }
                  onChange={(checked: boolean) =>
                    checked
                      ? setItemsChecked([...itemsChecked, item])
                      : setItemsChecked(
                        itemsChecked.filter((x: any) => x.skuId !== item.skuId)
                      )
                  }
                ></Checkbox>
              </>
            )}
          </div>
        ),
    },
    {
      Key: 'isArchived',
      Label: t('Status'),
      width: '12%',
      Render: (item: any) =>
        item.isArchived && item.isLicenseExpired ? (
          <span className="badge bg-danger">
            <FontAwesomeIcon icon={icons.ARCHIVED} className="me-2" />
            {t('Archived')}
          </span>
        ) : item.isArchived ? (
          <span className="badge bg-danger">
            <FontAwesomeIcon icon={icons.ARCHIVED} className="me-2" />
            {t('Archived')}
          </span>
        ) : item.isLicenseExpired ? (
          <span className="badge bg-pink">
            <FontAwesomeIcon
              icon={icons.SKU_LICENSE_EXPIRED}
              className="me-2"
            />
            {t('Expired')}
          </span>
        ) : (
          <span className="badge bg-success">{t('Live')}</span>
        ),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el: any) => filterChange(col.Key, el.target.value)}
            >
              <option value="">{t('All')}</option>
              <option value="Live">{t('Live')}</option>
              <option value="Archived">{t('Archived')}</option>
              <option value="Expired">{t('Expired')}</option>
            </select>
          );
        },
      },
    },
  ];

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = suffixColumns
    .map((x) => columnsConfig.find((y: any) => y.Key === x))
    .filter((x) => x);
  const columns = visibleColumns
    .map((x) => columnsConfig.find((y: any) => y.Key === x))
    .filter((x) => x);

  // return an alaska table
  return skus ? (
    <KwPanel className="mb-4">
      <header className="mb-2">
        <h2 className="no-margin">{t('Game SKUs')}</h2>
        {!locked && (
          <div>
            {hasPermissionForCompany(
              currentUser,
              permissionTypes.MANAGE_GAMES,
              publisherCompanyId
            ) && (
                <Button
                  className="btn btn-primary me-2"
                  onClick={onAddSku}
                  text={t('Add SKU')}
                  testId="add-sku-button"
                />
              )}
          </div>
        )}
      </header>
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={skus.data.map((x: any) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={skus.totalRowCount}
        filteredRowCount={skus.filteredRowCount}
        scopes={skus.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filterChange={filterChange}
        setBulkActionsSlideoutOpen={
          userIs(currentUser, userTypes.UPLOADER) && handleBulkActions
        }
        itemsChecked={itemsChecked}
        bulkActionTitle={
          itemsChecked?.length === 0
            ? t('Bulk actions - first tick some skus')
            : t('Bulk actions')
        }
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default SkuList;
